import React, { useState } from 'react';
import { SmartDevice, DeviceType } from './interfaces/types';
import { 
  Plus, 
  Lightbulb, 
  PlusCircle, 
  Thermometer, 
  Speaker, 
  Tv, 
  Coffee, 
  LayoutGrid,
  Power
} from 'lucide-react';

interface ConnectedDevicesProps {
  devices: SmartDevice[];
  onAddDevice: (device: SmartDevice) => void;
}

const deviceIcons: Record<DeviceType, React.ReactNode> = {
  LIGHT: <Lightbulb size={20} className="text-yellow-500" />,
  PLUG: <Power size={20} className="text-green-500" />,
  BLIND: <LayoutGrid size={20} className="text-blue-500" />,
  THERMOSTAT: <Thermometer size={20} className="text-red-500" />,
  SPEAKER: <Speaker size={20} className="text-purple-500" />,
  TV: <Tv size={20} className="text-gray-700" />,
  COFFEE_MACHINE: <Coffee size={20} className="text-amber-700" />
};

const locationOptions = ['Bedroom', 'Bathroom', 'Kitchen', 'Living Room', 'Hallway', 'Dining Room'];
const deviceBrands: Record<DeviceType, string[]> = {
  LIGHT: ['Philips Hue', 'LIFX', 'GE', 'Nanoleaf', 'Wyze'],
  PLUG: ['TP-Link', 'Amazon', 'Wemo', 'Gosund', 'Kasa'],
  BLIND: ['Lutron', 'Somfy', 'Hunter Douglas', 'IKEA', 'Serena'],
  THERMOSTAT: ['Nest', 'Ecobee', 'Honeywell', 'Emerson', 'Tado'],
  SPEAKER: ['Sonos', 'Bose', 'JBL', 'Google', 'Amazon'],
  TV: ['Samsung', 'LG', 'Sony', 'Vizio', 'TCL'],
  COFFEE_MACHINE: ['Smart Home', 'Hamilton Beach', 'Keurig', 'Nespresso', 'Mr. Coffee']
};

const ConnectedDevices: React.FC<ConnectedDevicesProps> = ({ devices, onAddDevice }) => {
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [newDevice, setNewDevice] = useState<Partial<SmartDevice>>({
    type: 'LIGHT',
    name: '',
    brand: 'Philips Hue',
    location: 'Bedroom'
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'type' && value in deviceBrands) {
      setNewDevice(prev => ({ 
        ...prev, 
        type: value as DeviceType,
        brand: deviceBrands[value as DeviceType][0]
      }));
    } else {
      setNewDevice(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newDevice.name || !newDevice.type || !newDevice.brand || !newDevice.location) {
      return; // Don't submit if required fields are missing
    }
    
    // Create a full device object
    const deviceToAdd: SmartDevice = {
      id: `${newDevice.type?.toLowerCase()}-${Date.now()}`,
      name: newDevice.name,
      type: newDevice.type as DeviceType,
      brand: newDevice.brand,
      location: newDevice.location,
      status: 'pairing', // Start in pairing mode
      lastSeen: new Date(),
      currentState: { isOn: false } // Default to off
    };
    
    onAddDevice(deviceToAdd);
    
    // Reset form
    setNewDevice({
      type: 'LIGHT',
      name: '',
      brand: 'Philips Hue',
      location: 'Bedroom'
    });
    setIsAddingDevice(false);
  };
  
  const formatTime = (date?: Date): string => {
    if (!date) return 'Unknown';
    
    // Return time like "5:20:30 PM"
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-5 mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Connected Devices</h3>
        <button 
          onClick={() => setIsAddingDevice(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center px-3 py-2 text-sm transition-colors"
        >
          <Plus size={18} className="mr-1" />
          Add Device
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {devices.map(device => (
          <div key={device.id} className="border rounded-lg p-4 flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-full">
              {deviceIcons[device.type] || <Lightbulb size={20} className="text-gray-500" />}
            </div>
            
            <div className="flex-1">
              <div className="flex items-center">
                <h4 className="font-medium">{device.name}</h4>
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                  device.status === 'connected' ? 'bg-green-100 text-green-800' : 
                  device.status === 'pairing' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-red-100 text-red-800'
                }`}>
                  {device.status}
                </span>
              </div>
              <p className="text-sm text-gray-600">{device.brand}</p>
              <div className="text-xs text-gray-500 mt-1">
                <div>Location: {device.location}</div>
                <div>Last seen: {formatTime(device.lastSeen)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {devices.length === 0 && !isAddingDevice && (
        <div className="text-center py-8">
          <div className="bg-gray-100 inline-block p-4 rounded-full mb-3">
            <PlusCircle size={32} className="text-gray-400" />
          </div>
          <h4 className="text-lg font-medium mb-1">No devices yet</h4>
          <p className="text-gray-500 mb-4">Add your first smart home device to get started</p>
          <button 
            onClick={() => setIsAddingDevice(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center px-4 py-2 mx-auto"
          >
            <Plus size={18} className="mr-2" />
            Add Device
          </button>
        </div>
      )}
      
      {isAddingDevice && (
        <div className="bg-gray-50 rounded-lg p-4 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium">Add New Device</h4>
            <button 
              onClick={() => setIsAddingDevice(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={newDevice.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Bed Pressure Sensor"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Type
                </label>
                <select
                  name="type"
                  value={newDevice.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                >
                  <option value="LIGHT">Light</option>
                  <option value="PLUG">Smart Plug</option>
                  <option value="BLIND">Blinds</option>
                  <option value="THERMOSTAT">Thermostat</option>
                  <option value="SPEAKER">Speaker</option>
                  <option value="TV">TV</option>
                  <option value="COFFEE_MACHINE">Coffee Machine</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand
                </label>
                <select
                  name="brand"
                  value={newDevice.brand}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                >
                  {newDevice.type && deviceBrands[newDevice.type as DeviceType]?.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  name="location"
                  value={newDevice.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                >
                  {locationOptions.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setIsAddingDevice(false)}
                className="mr-2 px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Add Device
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ConnectedDevices; 